import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ENTRY_STATUS_LABELS } from '@/lib/entry-status'
import { cn } from '@/lib/utils'
import type { CheckedInExhibit } from '@/hooks/useEntryIntake'
import type { ExhibitorEntrySummary } from '@/pages/checkin/types'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { EntryLabelPrintControl, type LabelPrintShow } from './EntryLabelPrintControl'

export type SelectedExhibitorEntriesListProps = {
  labelPrintShow?: LabelPrintShow
  entries: ExhibitorEntrySummary[]
  checkedInExhibits: CheckedInExhibit[]
  exhibitorId: string | null
  exhibitorName: string | null
  exhibitorPersonName: string | null
  currentShowStatus: 'draft' | 'accepting_entries' | 'judging' | 'finalized' | null
  isOnline: boolean
  entryPermissionDisabled: boolean
  entryPermissionReason?: string
  /** Why this entrant's entries cannot be checked in. Other entry actions stay available. */
  checkInRestriction?: string
  onManualCheckIn: (entry: ExhibitorEntrySummary) => Promise<unknown> | void
  checkingInEntryId: string | null
  checkInPending: boolean
  hideEntryCodes: boolean
  highlightEntryId: string | null
  onDeleteEntry?: (entry: ExhibitorEntrySummary) => void
  deleteEntryPendingId?: string | null
  canDeleteEntries?: boolean
  deleteDisabledReason?: string
}

export function SelectedExhibitorEntriesList({
  labelPrintShow,
  entries,
  checkedInExhibits,
  exhibitorId,
  exhibitorName,
  exhibitorPersonName,
  currentShowStatus,
  isOnline,
  entryPermissionDisabled,
  entryPermissionReason,
  checkInRestriction,
  onManualCheckIn,
  checkingInEntryId,
  checkInPending,
  hideEntryCodes,
  highlightEntryId,
  onDeleteEntry,
  deleteEntryPendingId = null,
  canDeleteEntries = true,
  deleteDisabledReason,
}: SelectedExhibitorEntriesListProps) {
  const currentSlug = useShowSlug()
  const nextEntries = useMemo(
    () => entries.filter((entry): entry is ExhibitorEntrySummary => Boolean(entry?.id)).slice(0, 5),
    [entries]
  )

  if (nextEntries.length === 0) {
    return null
  }

  const showAllowsCheckIn = currentShowStatus === 'accepting_entries'
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground">Recent entries</p>
      <ul className="space-y-2">
        {nextEntries.map(entry => {
          const isNotEntered = entry.status === 'not_entered'
          const isDisqualified = entry.status === 'disqualified'
          const localCheckedIn = checkedInExhibits.find(
            item => item.entryId === entry.id || item.entryCode === entry.entryCode
          )
          const checkedIn: CheckedInExhibit | null = isNotEntered
            ? null
            : (localCheckedIn ??
              (entry.exhibitId
                ? {
                    exhibitId: entry.exhibitId,
                    entryId: entry.id,
                    entryCode: entry.entryCode,
                    classId: entry.class?.id ?? null,
                    classCode: entry.class?.code ?? null,
                    className: entry.class?.name ?? null,
                    exhibitorId,
                    exhibitorName: exhibitorName ?? exhibitorPersonName ?? null,
                    checkedInAt: new Date().toISOString(),
                    remappedFrom: null,
                  }
                : null))

          const hasCanonicalExhibitId =
            Boolean(entry.entryCode) && !entry.entryCode?.startsWith('TMP-')
          const exhibitIdLabel = isNotEntered
            ? 'Marked Not Entered'
            : checkedIn?.exhibitId
              ? `Exhibit ID ${entry.entryCode ?? '—'}`
              : hasCanonicalExhibitId
                ? 'Exhibit ID pending'
                : 'Exhibit ID pending'

          const isCheckingIn = checkInPending && checkingInEntryId === entry.id
          const otherCheckInInFlight =
            checkInPending && (checkingInEntryId === null || checkingInEntryId !== entry.id)

          let checkInDisabledReason: string | undefined
          if (isNotEntered) {
            checkInDisabledReason = 'Not Entered entries cannot be checked in.'
          } else if (!checkedIn?.exhibitId) {
            if (!showAllowsCheckIn) {
              checkInDisabledReason = 'Check-in is available when the show is accepting entries.'
            } else if (!isOnline) {
              checkInDisabledReason = 'Go online before checking in entries.'
            } else if (entryPermissionDisabled) {
              checkInDisabledReason =
                entryPermissionReason ?? 'Checking in entries requires additional permissions.'
            } else if (checkInRestriction) {
              checkInDisabledReason = checkInRestriction
            } else if (!hasCanonicalExhibitId) {
              checkInDisabledReason = 'Check-in requires a canonical Exhibit ID.'
            } else if (otherCheckInInFlight) {
              checkInDisabledReason = 'Another check-in is in progress.'
            }
          }

          const checkInDisabled = Boolean(checkInDisabledReason) || isCheckingIn
          const entryDetailPath =
            entry.id && currentSlug ? showAware(`/entries/${entry.id}`, { currentSlug }) : null

          let deleteDisabledReasonValue: string | undefined
          if (!canDeleteEntries) {
            deleteDisabledReasonValue =
              deleteDisabledReason ?? 'Entries cannot be deleted after finalization.'
          } else if (entryPermissionDisabled) {
            deleteDisabledReasonValue =
              entryPermissionReason ?? 'Deleting entries requires additional permissions.'
          }
          const deleting = deleteEntryPendingId === entry.id
          const deleteDisabled =
            deleting || Boolean(deleteDisabledReasonValue) || typeof onDeleteEntry !== 'function'

          return (
            <li
              key={entry.id}
              className={cn(
                'space-y-2 rounded-md border p-3 text-xs transition-colors',
                highlightEntryId === entry.id
                  ? 'border-primary bg-primary/5'
                  : isNotEntered
                    ? 'border-amber-300 bg-amber-50/80'
                    : 'border-border/60 bg-background'
              )}
            >
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{exhibitIdLabel}</span>
                    {!hideEntryCodes && (
                      <span className="font-mono text-xs text-muted-foreground">
                        {entry.entryCode}
                      </span>
                    )}
                    {isDisqualified ? (
                      <Badge variant="destructive">{ENTRY_STATUS_LABELS.disqualified}</Badge>
                    ) : (
                      checkedIn?.exhibitId && <Badge variant="secondary">Checked in</Badge>
                    )}
                    {isNotEntered && (
                      <Badge
                        variant="outline"
                        className="border-amber-400 bg-amber-50 text-amber-900"
                      >
                        Not Entered
                      </Badge>
                    )}
                    {entry.overrideDuplicateReason ? (
                      <Badge
                        variant="outline"
                        className="max-w-[200px] truncate border-amber-400 text-amber-900"
                        title={entry.overrideDuplicateReason}
                      >
                        Override: {entry.overrideDuplicateReason}
                      </Badge>
                    ) : null}
                  </div>
                  <span className="text-muted-foreground line-clamp-1">
                    {entry.class?.name ?? 'Class pending'}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {labelPrintShow && (
                    <EntryLabelPrintControl
                      entry={{ ...entry, exhibitId: checkedIn?.exhibitId ?? null }}
                      show={labelPrintShow}
                    />
                  )}
                  {entryDetailPath ? (
                    <Button asChild size="sm" variant="ghost">
                      <Link to={entryDetailPath}>View entry</Link>
                    </Button>
                  ) : null}
                  {!checkedIn?.exhibitId && !isNotEntered && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={checkInDisabled}
                      title={checkInDisabledReason}
                      className="disabled:opacity-90 disabled:text-foreground"
                      onClick={() => void onManualCheckIn(entry)}
                    >
                      {isCheckingIn ? 'Checking…' : 'Check in'}
                    </Button>
                  )}
                  {onDeleteEntry ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={deleteDisabled}
                      title={deleteDisabledReasonValue}
                      onClick={() => {
                        if (!deleteDisabled) {
                          onDeleteEntry(entry)
                        }
                      }}
                    >
                      {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
